import React, { useEffect, useMemo, useState } from "react";
import { deleteContactMessage, listContactMessages, updateContactMessage } from "./api";
import styles from "./List.module.css";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusStyle = (status) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 12px",
  borderRadius: "999px",
  backgroundColor: status === "Read" ? "#dcfce7" : "#fef3c7",
  color: status === "Read" ? "#166534" : "#92400e",
  fontWeight: 700,
  fontSize: "0.78rem",
  letterSpacing: "0.02em",
});

export default function ContactMessagesList() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadMessages = async () => {
    try {
      const res = await listContactMessages();
      setItems(res.items || []);
      setError("");
    } catch (e) {
      setError(e.message || "Failed to load messages");
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus = statusFilter === "All" || item.status === statusFilter;
      const haystack = `${item.name || ""} ${item.email || ""} ${item.subject || ""} ${item.message || ""}`.toLowerCase();
      const matchesSearch = query === "" || haystack.includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [items, search, statusFilter]);

  const toggleStatus = async (message) => {
    const nextStatus = message.status === "Read" ? "Unread" : "Read";
    try {
      await updateContactMessage(message.id, nextStatus);
      setItems((current) =>
        current.map((item) =>
          item.id === message.id ? { ...item, status: nextStatus } : item
        )
      );
      if (selectedMessage && selectedMessage.id === message.id) {
        setSelectedMessage({ ...selectedMessage, status: nextStatus });
      }
    } catch (e) {
      setError(e.message || "Failed to update message");
    }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await deleteContactMessage(id);
      setItems((current) => current.filter((item) => item.id !== id));
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage(null);
      }
    } catch (e) {
      setError(e.message || "Failed to delete message");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.headerRow}>
        <div className={styles.title}>Contact Messages</div>
      </div>

      <div style={{ padding: "1.5rem 1.25rem 0" }}>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, subject, or message"
            style={{
              flex: "1 1 280px",
              maxWidth: "420px",
              padding: "0.8rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "0.95rem",
            }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "0.8rem 1rem",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "0.95rem",
              background: "#fff",
            }}
          >
            <option value="All">All statuses</option>
            <option value="Unread">Unread</option>
            <option value="Read">Read</option>
          </select>
        </div>
      </div>

      {error && <div className={styles.empty} style={{ color: "#b91c1c" }}>{error}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.th}>#</th>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Email</th>
              <th className={styles.th}>Phone</th>
              <th className={styles.th}>Subject</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Sent</th>
              <th className={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={8}>No contact messages found.</td>
              </tr>
            ) : (
              filteredItems.map((it, idx) => (
                <tr key={it.id}>
                  <td className={styles.td}>{idx + 1}</td>
                  <td className={styles.td}>{it.name}</td>
                  <td className={styles.td}>{it.email}</td>
                  <td className={styles.td}>{it.phone}</td>
                  <td className={styles.td}>{it.subject || "General enquiry"}</td>
                  <td className={styles.td}>
                    <span style={getStatusStyle(it.status)}>{it.status}</span>
                  </td>
                  <td className={styles.td}>{formatDate(it.created_at)}</td>
                  <td className={styles.td}>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.btn} ${styles.btnEdit}`}
                        onClick={() => setSelectedMessage(it)}
                      >
                        View
                      </button>
                      <button
                        className={`${styles.btn} ${styles.btnEdit}`}
                        onClick={() => toggleStatus(it)}
                      >
                        {it.status === "Read" ? "Mark Unread" : "Mark Read"}
                      </button>
                      <button
                        className={`${styles.btn} ${styles.btnDelete}`}
                        disabled={busyId === it.id}
                        onClick={() => handleDelete(it.id)}
                      >
                        {busyId === it.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedMessage && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "1rem" }}>
          <div style={{ width: "min(760px, 100%)", background: "#fff", borderRadius: "14px", padding: "1.5rem", boxShadow: "0 22px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, color: "#111827" }}>Message Details</h3>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDelete}`}
                onClick={() => setSelectedMessage(null)}
              >
                Close
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.2rem" }}>
              <div>
                <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Name</div>
                <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.75rem", color: "#111827" }}>{selectedMessage.name}</div>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Email</div>
                <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.75rem", color: "#111827" }}>{selectedMessage.email}</div>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Phone</div>
                <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.75rem", color: "#111827" }}>{selectedMessage.phone}</div>
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Status</div>
                <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.75rem" }}><span style={getStatusStyle(selectedMessage.status)}>{selectedMessage.status}</span></div>
              </div>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Subject</div>
              <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.75rem", color: "#111827" }}>{selectedMessage.subject || "General enquiry"}</div>
            </div>

            <div>
              <div style={{ fontWeight: 700, color: "#374151", marginBottom: "0.35rem" }}>Message</div>
              <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "0.9rem 1rem", lineHeight: 1.7, color: "#111827", whiteSpace: "pre-wrap" }}>
                {selectedMessage.message}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
